import { useTranslation } from 'react-i18next';
import { formatDate } from '../i18n';
import { isAdmin, isEditor, useStore } from '../store/store';
import { deleteRecord, resolveRecord, setRecordStatus } from './actions';
import { useRecordsByGuid } from './hooks';
import type { DataRecord } from './types';

/**
 * The review workflow for one record:
 *   editors  idea ⇄ proposed
 *   admins   approve / back to proposed, reject (reset to Unity), resolve conflicts
 * Approved changes go into the next change package (Admin → Export to Unity).
 */
export function StatusActions({ record: r }: { record: DataRecord }) {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const users = useStore((s) => s.users);
  const admin = isAdmin(user);
  const byGuid = useRecordsByGuid();
  // A new asset (D3): exists only on the web until a change package creates it in Unity.
  const isNew = !r.guid && !!r.pendingPath;
  const pending = isNew || (!!r.guid && r.syncState !== 'in_sync' && r.syncState !== 'missing_in_unity');
  // Gone from Unity (deleted there): only an admin can drop it from the tool.
  const isGone = admin && !!r.guid && r.syncState === 'missing_in_unity';
  const canDelete =
    isGone ||
    (isNew && (admin || (r.createdBy === user?.id && (r.status === 'idea' || r.status === 'proposed'))));
  const who = (id: string) => users[id]?.name ?? t('common.someone');

  const reject = () => {
    if (confirm(t('data.review.confirmReject', { name: r.name }))) void resolveRecord(r.id, 'take_unity');
  };

  const buttons: React.ReactNode[] = [];
  if (isEditor(user) && !admin && (r.status === 'idea' || r.status === 'proposed')) {
    const next = r.status === 'idea' ? 'proposed' : 'idea';
    buttons.push(
      <button key="toggle" onClick={() => void setRecordStatus(r, next)}>
        {t(`data.review.to_${next}`)}
      </button>,
    );
  }
  if (admin && isNew) {
    buttons.push(
      r.status !== 'approved' ? (
        <button key="approve" className="primary" onClick={() => void setRecordStatus(r, 'approved')}>
          {t('data.review.approve')}
        </button>
      ) : (
        <button key="unapprove" onClick={() => void setRecordStatus(r, 'proposed')}>
          {t('data.review.unapprove')}
        </button>
      ),
    );
  } else if (admin && pending) {
    if (r.syncState === 'conflict') {
      buttons.push(
        <button key="keep" onClick={() => void resolveRecord(r.id, 'keep_design')}>
          {t('data.review.keepDesign')}
        </button>,
      );
    } else if (r.status !== 'approved') {
      buttons.push(
        <button key="approve" className="primary" onClick={() => void setRecordStatus(r, 'approved')}>
          {t('data.review.approve')}
        </button>,
      );
    } else {
      buttons.push(
        <button key="unapprove" onClick={() => void setRecordStatus(r, 'proposed')}>
          {t('data.review.unapprove')}
        </button>,
      );
    }
    buttons.push(
      <button key="reject" className="danger" onClick={reject}>
        {r.syncState === 'conflict' ? t('data.review.takeUnity') : t('data.review.reject')}
      </button>,
    );
  }

  if (canDelete)
    buttons.push(
      <button
        key="delete"
        className="danger"
        onClick={() => {
          const msg = isGone
            ? t('data.review.confirmRemoveGone', { name: r.name })
            : t('data.create.confirmDelete', { name: r.name });
          if (confirm(msg)) void deleteRecord(r.id);
        }}
      >
        {isGone ? t('data.review.removeGone') : t('common.delete')}
      </button>,
    );

  const info: string[] = [];
  if (isNew)
    info.push(
      t('data.create.pending', { path: r.pendingPath, template: byGuid[r.template]?.name ?? r.template }),
    );
  if (pending && r.changedBy) info.push(t('data.review.changedBy', { name: who(r.changedBy) }));
  if (r.status === 'approved' && r.approvedBy)
    info.push(t('data.review.approvedBy', { name: who(r.approvedBy) }));
  if (r.status === 'approved' && r.exportId && r.exportedAt)
    info.push(t('data.review.exported', { date: formatDate(r.exportedAt) }));

  if (!buttons.length && !info.length) return null;
  return (
    <div className="insp-actions record-actions">
      {buttons}
      {info.length > 0 && <span className="muted small">{info.join(' · ')}</span>}
    </div>
  );
}
